import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { deliveryQualityService } from 'services/deliveryQualityService';
import { Card } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { Badge } from '../../../../components/ui/Badge';
import { Table, THead, TBody, TR, TH, TD } from '../../../../components/ui/Table';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { CardTableSkeleton } from './TabSkeletons';
import { formatPercent as fmtPct } from '../../../../utils/formatters';

// Tono del score → token semántico (mismo criterio en desktop y mobile).
const scoreTone = (score) => {
  if (score < 60) return 'error';
  if (score < 75) return 'brand';
  if (score < 85) return 'warning';
  return 'success';
};

const ScoreBadge = ({ score }) => (
  <Badge tone={scoreTone(score)}>{(score ?? 0).toFixed(1)}</Badge>
);

const MunicipalityTab = ({ filters }) => {
  const { t } = useTranslation('deliveryquality');
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('avg_score');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    deliveryQualityService
      .getByMunicipality({ ...filters, limit: 200, min_count: 5, sort_by: 'avgScore', sort_dir: 1 })
      .then((r) => {
        if (alive) setRows(r.data?.data || []);
      })
      .catch((e) => alive && setError(e?.message || 'Error'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [filters]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = q
      ? rows.filter((r) =>
          (r.municipality_name || r.key || '').toString().toLowerCase().includes(q),
        )
      : [...rows];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [rows, search, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  if (loading) {
    return <CardTableSkeleton rows={10} columns={9} />;
  }
  if (error) {
    return (
      <div className="rounded-lg border border-status-error-soft bg-status-error-soft text-status-error px-4 py-3 text-sm">
        {error}
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="rounded-lg border border-border bg-bg-muted text-fg-muted px-4 py-3 text-sm">
        {t('Sin datos para el rango.')}
      </div>
    );
  }

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-bg-muted flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <span className="text-sm font-semibold text-fg">
          {t('Ranking por comuna ({{n}})', { n: filtered.length })}
        </span>
        <div className="relative w-full sm:max-w-[260px]">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
          <Input
            type="text"
            placeholder={t('Buscar comuna…')}
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isMobile ? (
        <div className="p-3 flex flex-col gap-2">
          {filtered.map((r) => (
            <div key={r.key || 'sin-comuna'} className="rounded-lg border border-border p-3 flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-fg">
                  {r.municipality_name || r.key || <em className="text-fg-subtle">{t('Sin comuna')}</em>}
                </span>
                <ScoreBadge score={r.avg_score} />
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-fg-muted">
                <span>{t('# entregas')}: <strong className="text-fg tabular-nums">{r.total_evaluations}</strong></span>
                <span>{t('Tarde')}: <strong className="text-fg tabular-nums">{r.late_count}</strong></span>
                <span>% RUT: <strong className="text-fg tabular-nums">{fmtPct(r.pass_rates?.rut)}</strong></span>
                <span>{t('% Receptor')}: <strong className="text-fg tabular-nums">{fmtPct(r.pass_rates?.receiver)}</strong></span>
                <span>{t('% Distancia')}: <strong className="text-fg tabular-nums">{fmtPct(r.pass_rates?.distance)}</strong></span>
                <span>{t('% Puntualidad')}: <strong className="text-fg tabular-nums">{fmtPct(r.pass_rates?.timeliness)}</strong></span>
                <span>{t('% Imágenes IA')}: <strong className="text-fg tabular-nums">{fmtPct(r.pass_rates?.images_ai)}</strong></span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="max-h-[600px] overflow-auto">
          <Table>
            <THead className="sticky top-0 z-[1]">
              <TR>
                <TH>{t('Comuna')}</TH>
                <TH className="text-right cursor-pointer" onClick={() => handleSort('total_evaluations')}>
                  {t('# entregas')}
                </TH>
                <TH className="text-right cursor-pointer" onClick={() => handleSort('avg_score')}>
                  {t('Score prom')}
                </TH>
                <TH className="text-right">% RUT</TH>
                <TH className="text-right">{t('% Receptor')}</TH>
                <TH className="text-right">{t('% Distancia')}</TH>
                <TH className="text-right">{t('% Puntualidad')}</TH>
                <TH className="text-right" title={t('% de entregas con verdict ENTREGA VALIDADA')}>{t('% Imágenes IA')}</TH>
                <TH className="text-right cursor-pointer" onClick={() => handleSort('late_count')}>
                  {t('Tarde')}
                </TH>
              </TR>
            </THead>
            <TBody>
              {filtered.map((r) => (
                <TR key={r.key || 'sin-comuna'}>
                  <TD>{r.municipality_name || r.key || <em className="text-fg-subtle">{t('Sin comuna')}</em>}</TD>
                  <TD className="text-right tabular-nums">{r.total_evaluations}</TD>
                  <TD className="text-right"><ScoreBadge score={r.avg_score} /></TD>
                  <TD className="text-right tabular-nums">{fmtPct(r.pass_rates?.rut)}</TD>
                  <TD className="text-right tabular-nums">{fmtPct(r.pass_rates?.receiver)}</TD>
                  <TD className="text-right tabular-nums">{fmtPct(r.pass_rates?.distance)}</TD>
                  <TD className="text-right tabular-nums">{fmtPct(r.pass_rates?.timeliness)}</TD>
                  <TD className="text-right tabular-nums">{fmtPct(r.pass_rates?.images_ai)}</TD>
                  <TD className="text-right tabular-nums">{r.late_count}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      )}
    </Card>
  );
};

export default MunicipalityTab;
